<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>

        <div
            v-if="initialLoading"
            key="initialLoading"
            class="animate__animated animate__fadeIn animate_fadeOut animate__fast"
        >
            <!-- <busy-spinner inner-class="my-5" text="Loading Users..."></busy-spinner> -->
            <b-skeleton-table
                :rows="10"
                :columns="fields.length"
                show-footer
                :table-props="{ bordered: true }"
            ></b-skeleton-table>
        </div>

        <div 
            v-if="!initialLoading"
            key="initialLoadingDone"
        >

            <div class="d-flex flex-wrap justify-content-between align-items-end mb-3 animate__animated animate__fast animate__fadeInUp">
                <base-search-box
                    :value="currentFilterValues.searchFilter"
                    ref="searchBox"
                    placeholder="Quick Search..."
                    @search="setFilterAndSearch('searchFilter', $event)"
                    @reset="setFilterAndSearch('searchFilter', '')"
                ></base-search-box>
                <b-form inline class="mt-2">
                    <div class="align-self-end">
                        <b-button 
                            variant="soft-primary" 
                            :disabled="!showFilterReset" 
                            class="text-uppercase" 
                            @click="clearAllFilters"
                        >
                            Clear Filters
                        </b-button>
                    </div>
                    <base-filter-select
                        label="Filter By User Role"
                        v-model="currentFilterValues.userRoleFilter"
                        :reset-value="initialFilterValues.userRoleFilter"
                        :options="userRoleFilterOptions"
                        @change="getNewList"
                    ></base-filter-select>
                    <base-filter-select
                        label="Portal Access"
                        v-model="currentFilterValues.portalAccessFilter"
                        :reset-value="initialFilterValues.portalAccessFilter"
                        :options="portalAccessFilterOptions"
                        @change="getNewList"
                    ></base-filter-select>
                    <b-button
                        v-b-tooltip.hover title="Refresh List"
                        variant="light"
                        class="ml-2 align-self-end"
                        @click="refreshList(true)"
                    >
                        <b-icon icon="arrow-repeat"></b-icon>
                    </b-button>
                </b-form>
                <!-- <div v-if="!loading && totalRows" class="font-size-90 text-muted">{{totalRows}} Vendors</div> -->
                <!-- <b-button variant="success" @click="handleAdd">
                    <b-icon icon="plus-circle" class="mr-1"></b-icon>
                    Add Vendor
                </b-button> -->
            </div>

            <b-card 
                id="vendor-contact-table-card"
                class="shadow rounded-lg animate__animated animate__fadeIn animate__fast"
                no-body
            >
                <div
                    v-if="!loading && !totalRows"
                    class="d-flex justify-content-center align-items-center"
                    style="min-height: 300px;"
                >
                    <div class="text-center">
                        <div class="font-size-100 text-muted mb-4">
                            {{ hasAppliedFilters ? 'No users found.  Try a different search' : 'No users yet.  Add one?' }}
                        </div>
                        <b-button v-if="hasAppliedFilters" variant="soft-primary" @click="clearAllFilters">
                            Clear Search
                        </b-button>
                        <b-button v-if="!hasAppliedFilters" size="lg" variant="soft-success" @click="handleAdd">
                            Add User
                        </b-button>
                    </div>
                </div>

                <b-table 
                    v-else 
                    :items="itemList"
                    :fields="fields"
                    :busy="loading"
                    primary-key="id" 
                    no-local-sorting
                    no-sort-reset
                    :sort-by.sync="sortByField"
                    :sort-desc.sync="sortDesc"
                    @sort-changed="getNewList"
                    hover
                    class="no-top-border td-align-middle mb-0"
                    thead-class="overflow-hidden cells-align-middle"
                    head-variant="light"
                >
                    <!-- <template v-slot:table-busy>
                        <div class="text-center text-primary my-2">
                            <busy-spinner text="Loading..."></busy-spinner>
                        </div>
                    </template> -->
                    <template v-slot:cell(full_name)="data">
                        <b-link @click="handleUpdate(data.item.id)">{{ data.value }}</b-link>
                        <base-popover-icon 
                            v-if="data.item.manual_block" 
                            icon="exclamation-circle-fill" 
                            icon-variant="danger" 
                            title="Account On Hold"
                            class="ml-2"
                        >
                            This user's account has been placed on hold by Supplios.  Please contact Supplios support to remove this restriction.
                        </base-popover-icon>
                        <base-popover-icon 
                            v-if="data.item.email_issues_since" 
                            icon="envelope-fill" 
                            icon-variant="danger" 
                            title="Email Issues"
                            class="ml-2"
                        >
                            There are delivery issues with this user's email address.  Please contact Supplios support for more information.
                        </base-popover-icon>
                    </template>
                    <template #cell(company_name)="data">
                        <a v-if="data.item.company_url" :href="data.item.company_url">{{ data.value }}</a>
                        <span v-else>{{ data.value }}</span>
                    </template>
                    <template #cell(portal_access)="data">
                        <b-badge v-if="data.value === 'full'" key="portal-full" variant="soft-success" class="text-uppercase">Active</b-badge>
                        <b-badge v-else-if="data.value === 'open_invite'" key="portal-invited" variant="soft-primary" class="text-uppercase">Open Invite</b-badge>
                        <b-badge v-else-if="data.value === 'expired_invite'" key="portal-invite-expired" variant="soft-warning" class="text-uppercase">Expired Invite</b-badge>
                        <b-badge v-else-if="data.value === 'on_hold'" key="portal-on-hold" variant="soft-danger" class="text-uppercase">Deactivated</b-badge>
                        <b-badge v-else-if="data.value === 'invite_ready'" key="portal-invite-ready" variant="soft-info" class="text-uppercase">Invite Ready</b-badge>
                        <b-badge v-else-if="data.value === 'none'" key="portal-none" variant="soft-secondary" class="text-uppercase">No Access</b-badge>
                        <base-popover-icon 
                            v-if="data.item.manual_block" 
                            icon="exclamation-circle-fill" 
                            icon-variant="danger" 
                            title="Account On Hold"
                            class="ml-2"
                        >
                            This user's account has been placed on hold by Supplios.  Please contact Supplios support to remove this restriction.
                        </base-popover-icon>
                    </template>
                    <template #cell(is_full_portal_user)="data">
                        <b-badge :variant="data.value ? 'soft-success' : 'soft-danger'">
                            <b-icon :icon="data.value ? 'check' : 'x'" font-scale="1.5"></b-icon>
                            {{ data.value ? 'Yes' : 'No' }}
                        </b-badge>

                    </template>
                    <template #cell(created)="data">
                        {{ data.value | dateFilter }}
                    </template>
                    <template #cell(actions)="data">
                        <b-button-group size="sm">
                            <b-button variant="gray-info" @click="handleUpdate(data.item.id)">
                                View/Edit
                            </b-button>
                            <b-dropdown variant="gray-secondary" no-caret size="sm">
                                <template #button-content>
                                    <b-icon icon="three-dots"></b-icon>
                                </template>
                                <b-dropdown-item 
                                    :disabled="!currentUserInfo.is_tenant_superadmin || data.item.portal_access === 'on_hold'" 
                                    @click="startDeactivate(data.item.id)"
                                >
                                    <!-- just allowing for superadmins for now, until you have state machine stuff working -->
                                    <b-icon icon="trash" variant="danger" class="mr-2"></b-icon>
                                    Deactivate User
                                </b-dropdown-item>
                            </b-dropdown>
                        </b-button-group>
                        <!-- <b-button variant="gray-info" size="sm" @click="handleUpdate(data.item.id)">
                            View/Edit
                        </b-button> -->
                    </template>
                </b-table>
                <template #footer v-if="!loading && totalRows">
                    <base-table-pagination
                        :current-page.sync="currentPage"
                        :per-page.sync="perPage"
                        :total-items="totalRows"
                        show-per-page
                        item-name="contacts"
                        @update:perPage="getNewList"
                        @update:currentPage="getList"
                    ></base-table-pagination>
                </template>
            </b-card>

        </div>  <!-- End of area to hide when loading -->

        <!-- Update modal... not for creates -- that one should be in this components parent component -->
        <internal-tu-create-update-modal
            ref="contactModal"
            :enabled-role-options-create="enabledRoleOptionsCreate"
            :enabled-role-options-update="enabledRoleOptionsUpdate"
            :all-role-options="allRoleOptions"
            :allow-company-change="false"
            @update-complete="getNewList"
        ></internal-tu-create-update-modal>

    </div>
</template>

<script>
import { suppliosApi } from '../js/api-service';
import BaseTablePagination from './BaseTablePagination';
import { FormatDateHelper } from '../js/filters-helpers';
import BaseSearchBox from './BaseSearchBox';
import BaseFilterSelect from './BaseFilterSelect';
import InternalTuCreateUpdateModal from './InternalTuCreateUpdateModal';  // using this only for updates, not creates
import isEqual from 'lodash/isEqual';
import { splTableDefaults, currentUserInfoMixin } from '../js/custom-vue-mixins';

export default {
    name: 'InternalTuList',
    components: {
        BaseTablePagination,
        BaseSearchBox,
        BaseFilterSelect,
        InternalTuCreateUpdateModal,
    },
    mixins: [currentUserInfoMixin],
    props: {
        enabledRoleOptionsCreate: {  // TU roles that current TU is allowed to manage -- array of objects (keys 'id' and 'name') to pass to select for selecting a TenantUserRole
            // only the ones that make sense for CREATE (should have can_login=True)
            type: Array,
            required: true,
        },
        enabledRoleOptionsUpdate: {  // TU roles that current TU is allowed to manage -- array of objects (keys 'id' and 'name') to pass to select for selecting a TenantUserRole
            // only the ones that make sense for UPDATE (all internal roles)
            type: Array,
            required: true,
        },
        allRoleOptions: {  // all possible TU roles -- array of objects (keys 'id' and 'name') to pass to select for selecting a TenantUserRole
            type: Array,
            required: true,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            initialLoading: true,  // set to false after getting list the first time
            loading: false,  // loading signal for fetching list
            currentPage: 1,  // will get sync'd by base-table-pagination
            perPage: 25,  // will get sync'd by base-table-pagination
            totalRows: null,  // total items in list... need to set it after we get items from API, and also after filtering is applied with @filtered event
            itemList: [],   // list from API
            fields: [
                {
                    key: 'full_name',
                    label: 'Name',
                    sortable: true,
                    api_ordering_name: 'lname',  // override this in our custom method to set fname, lname 
                    tdClass: 'font-weight-bold'
                },/* {
                    key: 'company_name',
                    label: 'Company',
                    sortable: true,
                    api_ordering_name: 'company__name',
                }, */{
                    key: 'job_title',
                    label: 'Title',
                },{
                    key: 'work_location',
                    label: 'Work Location',
                },{
                    key: 'role_expanded.name',
                    label: 'Role',
                    sortable: true,
                    api_ordering_name: 'role__power_level',
                    class: "text-center",
                },/* {
                    key: 'is_full_portal_user',
                    label: 'Activated?',
                    class: 'text-center',
                }, */{
                    key: 'portal_access',
                    label: 'Portal Access',
                    class: 'text-center',
                },{
                    key: 'created',
                    label: 'Date Added',
                    sortable: true,
                    api_ordering_name: 'created',
                    class: 'text-center',
                },{
                    key: 'actions',
                    label: 'Actions',
                    class: 'text-center'
                }
            ],
            // filtering
            currentFilterValues: {},  // v-models will update this, set it from initialFilterValues in created hook
            initialFilterValues: {
                searchFilter: '',
                userRoleFilter: '',  // should be true, false, null -- if null, it won't send query param, and will get all
                portalAccessFilter: 'active',
            },
            userRoleFilterOptions: [
                {value: '', text: 'All'},
                {value: 'internal_superadmin', text: 'Super Admins'},
                {value: 'internal_admin', text: 'Admins'},
                {value: 'internal_sourcing', text: 'Full Users'},
                {value: 'internal_limited', text: 'Collaborators'},
                {value: 'internal_noaccess', text: 'No Access'},
            ],
            portalAccessFilterOptions: [
                {value: 'active', text: 'Active'},
                //{value: 'all', text: 'All'},  // this is confusing to show, when 'active' is the default
                {value: 'full', text: 'Full Access'},
                {value: 'open_invite', text: 'Open Invitations'},
                {value: 'expired_invite', text: 'Expired Invitations'},
                {value: 'invite_ready', text: 'Invite Ready'},
                {value: 'on_hold', text: 'On Hold / Deactivated'},
            ],
            hasAppliedFilters: false,  // toggle this on when you are showing filtered results so you know the difference between no results at all, and no results from search
            // for sorting:
            sortByField: 'created',  // sortByField and sortDesc will get .sync'd by b-table, then gets used in API call query params
            sortDesc: true,  // sort descending = true (adds '-' sign in backend), sort ascending = false (no minus sign)
        }
    },
    computed: {
        showFilterReset: function() {
            // returns BOOLEAN if any currentFilterValues are different than initialFilterValues
            let isSame = isEqual(this.currentFilterValues, this.initialFilterValues);
            return !isSame;  // show filter reset if current filter values are NOT SAME as initial filter values
        },
    },
    filters: {
        dateFilter: function(value) {
            return FormatDateHelper(value);
        },
    },
    created: function() {
        this.resetFilterValues();
        this.perPage = splTableDefaults.perPage;
    },
    mounted: function () {
        this.getList();
    },
    methods: {
        // PUBLIC METHOD YOU CAN CALL FROM PARENT TO REFRESH LIST, likely just as $refs.something.refreshList();
        refreshList: function(noFilterReset = false) {
            if (noFilterReset) {
                this.getNewList();  // if you specifically set noFilterReset to true (first positional arg), then don't reset filters (but do reset current page to 1)
            } else {
                this.clearAllFilters();   // this resets all filter values and reloads the list
            }
        },
        emitRefreshed: function() {
            // emits 'refreshed' event... in case other things need to be updated when this is refreshed
            this.$emit('refreshed');
        },
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        // methods to create/update companies using child CompanyCreateUpdateModal (removed from this component)
        handleUpdate: function(updateItemId) {
            this.$refs.contactModal.startUpdate(updateItemId);
        },
        handleAdd: function() { // only used if list is empty and user clicks on 'add one' button
            this.$refs.contactModal.startCreate();
        },
        resetFilterValues: function() {
            this.currentFilterValues = Object.assign({}, this.initialFilterValues);
            // new way
            const filterRefs = ['searchBox'];
            filterRefs.forEach(refName => {
                if (this.$refs[refName]) {
                    this.$refs[refName].reset();
                }
            });
        },
        setFilterAndSearch(filterKey, value) {
            // pass in which filter to set and the value
            console.log('filterKey and value', filterKey, value);
            this.currentFilterValues[filterKey] = value;
            this.getNewList();
        },
        clearAllFilters: function() {
            // clear/reset all filters and search
            this.resetFilterValues();
            this.getNewList();
        },
        getOrderingFieldString: function() {
            // look up what field name (string) we should use to send to API, in case key here is different than what API should order by
            // useful if you have serializer methods that get values from related objects... you can use double-underscore notation in DRF for ordering
            // also add '-' if sortDesc is true
            // FIRST, HANDLE SPECIAL CASE OF ORDERING BY 'full_name'
            if (this.sortByField === 'full_name') {
                if (this.sortDesc) {
                    return '-lname,-fname';
                } else {
                    return 'lname,fname';
                }
            }
            let apiOrderingName = this.fields.find(fld => fld.key === this.sortByField).api_ordering_name;
            return (this.sortDesc ? '-' : '') + apiOrderingName;
        },
        getNewList: function() {
            // call getList(), but must reset currentPage first
            // call this from any change to filters, sort, search, etc
            this.currentPage = 1;
            this.$nextTick(() => this.getList());
        },
        getList: function() {  
            this.loading = true;
            // axiosConfig['params'] is where we put search/filter params
            let filterParams = {  // grab the current search/filter values
                search: this.currentFilterValues.searchFilter || null,
                role__slug: this.currentFilterValues.userRoleFilter,
                portal_access: this.currentFilterValues.portalAccessFilter,
                // if you have more filters, put them here
            };
            let orderingParams = {
                ordering: this.getOrderingFieldString(),
            };
            let paginationParams = {
                page: this.currentPage,
                page_size: this.perPage,
            };
            let combinedParams = Object.assign({}, filterParams, orderingParams, paginationParams);
            let axiosConfig = {params: combinedParams};
            suppliosApi.internalTu.list(axiosConfig)
            .then(response => {
                this.totalRows = response.data.count;
                this.itemList = response.data.results;
                this.$emit('list-loaded', this.totalRows);  // emit total count to parent
                this.emitRefreshed();
                console.log("Got itemList from getList function");
                console.log(this.itemList);
                // need to set hasAppliedFilters after we get response, not before
                this.hasAppliedFilters = Object.values(filterParams).some(val => !!val);  // goes through all filter values, if any are not empty/null, returns true
            })
            .catch(error => {
                this.handleError(error, 'Error while loading itemList');
            })
            .finally(() => {
                console.log('hello from finally of getList');
                this.initialLoading = false;  // set flag to say we already loaded once
                this.loading = false;
            });
        },
        async startDeactivate(tuId) {
            // show modal to warn about deactivating, then if confirmed, call the real one
            const foundItem = this.itemList.find(tuObj => (tuObj.id === tuId));
            if (!foundItem) {
                return;
            }
            const tuFullName = foundItem.full_name || 'this user';
            const confirmOk = await this.$bvModal.msgBoxConfirm(`Are you sure you want to deactivate ${tuFullName}?  This is will remove them and prevent them from logging in.`, {
                title: 'Please Confirm',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Yes, Deactivate',
                cancelTitle: 'No, Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            });
            if (confirmOk) {
                await this.performDeactivate(tuId);
            }
        },
        async performDeactivate(tuId) {
            //this.loading = true;
            try {
                const { data } = await suppliosApi.internalTu.deactivate(tuId);
                // data is just a string message saying person was deleted
                this.$toast.success(data);
            } catch (err) {
                this.handleError(err, 'Error while deactivating this user.');
            }
            this.getList();  // this will refresh the list, but not reset the filters or current page
            // ugghh... should make getList async/await syntax....
            //this.loading = false;
        },
    }
}
</script>

<style>

</style>
